import * as opentype from "opentype.js";

const INK = "#2d2824";
const CREAM = "#fdfbf7";
const L = 320; // logo box in SVG units (matches foinn-logo.svg viewBox)

export const VARIANTS = {
  logo: { file: "a-logo", label: "Logo" },
  domain: { file: "b-logo-foinn-org", label: "Logo + Foinn.org" },
  full: { file: "c-logo-foinn-org-folk-tune-finder", label: "Logo + Foinn.org + Folk Tune Finder" },
};

export const FAVICONS = [16, 32, 48, 180, 192, 512];
export const PROFILES = [400, 800];

let assets = null;
export const loadAssets = async () => {
  if (assets) return assets;
  const buf = (u) => fetch(u).then((r) => r.arrayBuffer());
  const [svg, serif, sans] = await Promise.all([
    fetch("/foinn-logo.svg").then((r) => r.text()),
    buf("/fonts/CormorantGaramond-Bold.ttf").then(opentype.parse),
    buf("/fonts/Nunito-ExtraBold.ttf").then(opentype.parse),
  ]);
  assets = { mark: svg.match(/<svg[^>]*>([\s\S]*)<\/svg>/)[1], serif, sans };
  return assets;
};

const textPath = (font, text, size, opts) => {
  const p = font.getPath(text, 0, 0, size, opts);
  const b = p.getBoundingBox();
  return { p, b, w: b.x2 - b.x1, h: b.y2 - b.y1 };
};

const place = (t, x, top, fill) => {
  const p = t.p;
  p.commands.forEach((c) => {
    ["x", "x1", "x2"].forEach((k) => c[k] !== undefined && (c[k] += x - t.b.x1));
    ["y", "y1", "y2"].forEach((k) => c[k] !== undefined && (c[k] += top - t.b.y1));
  });
  p.fill = fill;
  return p.toSVG(2);
};

// pure vector: text is converted to outlines, so the SVG needs no fonts installed
export const buildSvg = ({ variant, dark, stacked }) => {
  const { mark, serif, sans } = assets;
  const ink = dark ? CREAM : INK;
  const sub = dark ? "#cfc6ba" : "#6e655b";
  const logo = (x, y) => `<g transform="translate(${x} ${y}) translate(-96 -96)">${mark}</g>`;
  if (variant === "logo") return wrap(L, L, logo(0, 0));

  const title = textPath(serif, "Foinn.org", L * 0.62);
  const tag = variant === "full" ? textPath(sans, "FOLK TUNE FINDER", L * 0.2, { letterSpacing: 0.175 }) : null;
  const gap = L * 0.1;
  const blockH = title.h + (tag ? gap + tag.h : 0);

  if (!stacked) {
    const tx = L * 1.2;
    const top = (L - blockH) / 2;
    const w = tx + Math.max(title.w, tag?.w || 0);
    let body = logo(0, 0) + place(title, tx, top, ink);
    if (tag) body += place(tag, tx, top + title.h + gap, sub);
    return wrap(w, L, body);
  }
  const w = Math.max(L, title.w, tag?.w || 0);
  const ty = L + L * 0.12;
  let body = logo((w - L) / 2, 0) + place(title, (w - title.w) / 2, ty, ink);
  if (tag) body += place(tag, (w - tag.w) / 2, ty + title.h + gap, sub);
  return wrap(w, ty + blockH, body);
};

const wrap = (w, h, body) => ({
  w,
  h,
  svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${w.toFixed(0)}" height="${h.toFixed(0)}" viewBox="0 0 ${w.toFixed(1)} ${h.toFixed(1)}">${body}</svg>`,
});

const loadImg = (svg) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });

// square raster: themed background, artwork fitted inside the padding
export const rasterSquare = async (art, size, { pad = 0.08, dark, transparent = false }) => {
  const img = await loadImg(art.svg);
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");
  if (!transparent) {
    ctx.fillStyle = dark ? INK : CREAM;
    ctx.fillRect(0, 0, size, size);
  }
  const box = size * (1 - pad * 2);
  const s = Math.min(box / art.w, box / art.h);
  const dw = art.w * s;
  const dh = art.h * s;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
  return c.toDataURL("image/png");
};

const dataUrlBytes = (u) => Uint8Array.from(atob(u.split(",")[1]), (ch) => ch.charCodeAt(0));

// .ico holding PNG images (supported by every current browser)
const buildIco = (pngs) => {
  const head = 6 + pngs.length * 16;
  const total = head + pngs.reduce((n, p) => n + p.bytes.length, 0);
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  dv.setUint16(2, 1, true);
  dv.setUint16(4, pngs.length, true);
  let off = head;
  pngs.forEach((p, i) => {
    const e = 6 + i * 16;
    out[e] = p.size >= 256 ? 0 : p.size;
    out[e + 1] = p.size >= 256 ? 0 : p.size;
    dv.setUint16(e + 4, 1, true);
    dv.setUint16(e + 6, 32, true);
    dv.setUint32(e + 8, p.bytes.length, true);
    dv.setUint32(e + 12, off, true);
    out.set(p.bytes, off);
    off += p.bytes.length;
  });
  return out;
};

const padFor = (variant, kind) => (kind === "profile" ? 0.16 : variant === "logo" ? 0.03 : 0.07);

export const variantFiles = async (variant, dark) => {
  const dir = `${dark ? "dark" : "light"}/${VARIANTS[variant].file}`;
  const square = buildSvg({ variant, dark, stacked: true });
  const files = {};
  const icoParts = [];
  for (const size of FAVICONS) {
    const url = await rasterSquare(square, size, { pad: padFor(variant, "favicon"), dark });
    const bytes = dataUrlBytes(url);
    files[`${dir}/favicon/favicon-${size}x${size}.png`] = bytes;
    if (size <= 48) icoParts.push({ size, bytes });
  }
  files[`${dir}/favicon/favicon.ico`] = buildIco(icoParts);
  for (const size of PROFILES) {
    const url = await rasterSquare(square, size, { pad: padFor(variant, "profile"), dark });
    files[`${dir}/profile/profile-${size}x${size}.png`] = dataUrlBytes(url);
  }
  const enc = new TextEncoder();
  if (variant === "logo") {
    files[`${dir}/vector/logo.svg`] = enc.encode(square.svg);
  } else {
    files[`${dir}/vector/logo-horizontal.svg`] = enc.encode(buildSvg({ variant, dark, stacked: false }).svg);
    files[`${dir}/vector/logo-stacked.svg`] = enc.encode(square.svg);
  }
  const big = await rasterSquare(square, 1024, { pad: 0.04, dark, transparent: true });
  files[`${dir}/vector/logo-transparent-1024.png`] = dataUrlBytes(big);
  return files;
};

export const LOGO_README = `Foinn.org logo pack

dark/  = for dark backgrounds (cream lettering)   light/ = for light backgrounds (ink lettering)
a-logo                                 the mark only
b-logo-foinn-org                       mark + Foinn.org
c-logo-foinn-org-folk-tune-finder      mark + Foinn.org + Folk Tune Finder

favicon/  16, 32, 48 (also bundled in favicon.ico), 180 (Apple touch icon), 192 and 512 (Android / PWA)
profile/  400 and 800 px squares, artwork kept inside the central circle for round avatars
vector/   SVG with text converted to outlines (no fonts needed) + a 1024 px transparent PNG
`;
